import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { useAccountInformationQuery } from 'src/views/uiflow/account/queries';
import { AccountDetailsData } from 'src/views/uiflow/account/types';
import { useQuery } from '@tanstack/react-query';
import { getAccountSubscriptionsByAccountId } from 'src/services/account';
import useImpersonateSessionData from 'src/@core/hooks/useImpersonateSessionData';

interface AccountContextType {
  accountData: AccountDetailsData | null;
  hasTmsConnect: boolean;
  hasAdditionalReferences: boolean;
  hasMicrosoftEntraIDIntegration: boolean;
  isLoading: boolean;
  refetch: () => void;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const auth = useAuth();
  const prod = useGetProductLevel();
  const { isImpersonating, impersonateData } = useImpersonateSessionData();

  const currentTenantId = (isImpersonating ? impersonateData?.tenantId : auth.user?.profile.tenantId) as string;

  const {
    data: rawAccountData,
    isLoading,
    refetch: queryRefetch
  } = useAccountInformationQuery(prod, {
    enabled: auth.isAuthenticated && !!auth.user,
    queryKeyOverride: ['accountInformation', prod, currentTenantId]
  });

  const accountData = rawAccountData ?? null;

  const { data: tenantSubscriptions } = useQuery({
    queryKey: ['tenantSubscriptions', currentTenantId],
    queryFn: async () => {
      if (!currentTenantId) return [];

      const response = await getAccountSubscriptionsByAccountId(currentTenantId, {
        filter: 'isActive eq true'
      });

      return response.data ?? [];
    },
    enabled: !!currentTenantId && auth.isAuthenticated
  });

  const hasTmsConnect = useMemo(
    () => tenantSubscriptions?.some(sub => sub.product?.code?.toLowerCase() === 'tmsconnect') ?? false,
    [tenantSubscriptions]
  );

  const hasAdditionalReferences = useMemo(
    () => tenantSubscriptions?.some(sub => sub.product?.code?.toLowerCase() === 'additionalreferences') ?? false,
    [tenantSubscriptions]
  );

  const hasMicrosoftEntraIDIntegration = useMemo(
    () => tenantSubscriptions?.some(sub => sub.product?.code?.toLowerCase() === 'microsoftentraidintegration') ?? false,
    [tenantSubscriptions]
  );

  const refetch = useCallback(() => {
    if (auth.isAuthenticated && auth.user) {
      queryRefetch();
    }
  }, [auth.isAuthenticated, auth.user, queryRefetch]);

  const contextValue = useMemo(
    () => ({ accountData, hasTmsConnect, hasAdditionalReferences, hasMicrosoftEntraIDIntegration, isLoading, refetch }),
    [accountData, hasTmsConnect, hasAdditionalReferences, hasMicrosoftEntraIDIntegration, isLoading, refetch]
  );

  return <AccountContext.Provider value={contextValue}>{children}</AccountContext.Provider>;
};

export const useAccountContext = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccountContext must be used within an AccountProvider');
  }

  return context;
};

export const useTmsConnect = (): Pick<AccountContextType, 'hasTmsConnect' | 'isLoading'> => {
  const { hasTmsConnect, isLoading } = useAccountContext();

  return { hasTmsConnect, isLoading };
};

export const useAdditionalReferences = (): Pick<AccountContextType, 'hasAdditionalReferences' | 'isLoading'> => {
  const { hasAdditionalReferences, isLoading } = useAccountContext();

  return { hasAdditionalReferences, isLoading };
};

export const useMicrosoftEntraIDIntegration = (): Pick<
  AccountContextType,
  'hasMicrosoftEntraIDIntegration' | 'isLoading'
> => {
  const { hasMicrosoftEntraIDIntegration, isLoading } = useAccountContext();

  return { hasMicrosoftEntraIDIntegration, isLoading };
};
